import { COORDINATE_SYSTEM, WebMercatorViewport } from '@deck.gl/core';
//@ts-ignore
import { TileLayer, _WMSLayer as WMSLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';
import { BackgroundType, DeckGLLayer, PortType, SwitchType } from 'types';
import { GeoJSONFeature } from 'types/geojson';

import { CoordinateArray } from './../types/index.d';

/**
 * Extracts unique layers used in the provided GeoJSON features and their corresponding indices.
 *
 * This function iterates through a list of GeoJSON features, identifies unique layer names
 * (found in the `properties.layer` field), and records the index of the first occurrence of each layer.
 *
 * @param features - An array of GeoJSONFeature objects to analyze.
 * @returns An array of objects, where each object contains:
 *   - `name`: The name of the layer.
 *   - `index`: The index of the first occurrence of the layer in the features.
 *
 * Example Output:
 * [
 *   { name: 'berth-layer', index: 0 },
 *   { name: 'pgb-layer', index: 12 },
 *   ...
 * ]
 */
export function getUsedLayer(features: GeoJSONFeature[]) {
    const layerMap = new Map(); // To store layer details: name, index, and features

    features.forEach((feature) => {
        const layerName = feature?.properties?.layer;
        const drawIndex = feature?.properties?.drawIndex;

        if (!layerMap.has(layerName)) {
            // Initialize the layer entry with name, index, and an empty features array
            layerMap.set(layerName, {
                name: layerName,
                index: drawIndex,
                features: [],
            });
        }

        // Add the feature to the corresponding layer
        layerMap.get(layerName).features.push(feature);
    });

    // Convert the map to an array of objects { name, index, features }
    return Array.from(layerMap.values());
}

/**
 * Updates the style and properties of a GeoJSON feature based on given parameters.
 *
 * @param feature - The GeoJSON feature to update.
 * @param type - The feature type used to determine visibility.
 * @param layer - The layer name to assign to the feature.
 * @param index - The index of the feature within its layer.
 * @param style - The style configuration object containing default styles.
 * @returns The updated GeoJSON feature.
 */
export function updatePortAreaFeatureStyle(
    switchs: SwitchType,
    feature: GeoJSONFeature,
    type: string,
    layer: string,
    drawIndex: number,
    clickIndex: number,
    withPlan: boolean,
    style: object,
    activePlanFeatures: string[]
) {
    //@ts-ignore
    let display = style.display;

    if (withPlan) {
        display = activePlanFeatures.includes(feature.properties?.name);
    } else if (switchs[type]) {
        display = switchs[type].value;
    } else if (switchs.other && switchs.other.options?.includes(type)) {
        display = switchs.other.value;
    } else if (!switchs[type]) {
        display = false;
    }

    //@ts-ignore
    // if ((style.default?.fill?.opacity === 0 || !style.default?.fill) && feature.geometry.type === 'Polygon') {
    //     //@ts-ignore
    //     feature.geometry.coordinates = feature.geometry.coordinates[0];
    //     //@ts-ignore
    //     feature.geometry.type = 'LineString';
    // }
    feature.properties = {
        ...feature.properties,
        layer: layer,
        drawIndex: drawIndex,
        clickIndex: clickIndex,
        withPlan: withPlan,
        visible: display,
        //@ts-ignore
        fill: style.fill || { color: '#000000', opacity: 0 },
        //@ts-ignore
        stroke: style.stroke || { color: '#000000', with: 0, opacity: 0 },
    };
    return feature;
}

/**
 * Updates the properties of a GeoJSON feature with port area details.
 *
 * @param id - The unique identifier for the port area.
 * @param feature - The GeoJSON feature to update. If null, no action is performed.
 * @param portArea - The port area details to apply to the feature.
 * @param type - The type of the port area feature.
 * @returns The updated GeoJSON feature, or null if the input feature is null.
 */
export function updateFeatureProperties(
    id: number | string,
    feature: GeoJSONFeature | null,
    featureData: any,
    type: string
): GeoJSONFeature | null {
    if (feature === null) {
        return feature;
    }
    feature.properties = {
        ...feature.properties,
        ...featureData,
        type,
        id,
    };
    return feature;
}

/**
 * Removes a layer from the provided array of Deck.gl layers based on its `id`.
 *
 * @param {DeckGLLayer[]} layers - The array of Deck.gl layers from which a layer will be removed.
 * @param {number | string | undefined} id - The `id` of the layer to be removed. If no valid `id` is provided, the original layers array is returned.
 *
 * @returns {DeckGLLayer[]} - The updated array of layers with the specified layer removed.
 */
export function removeLayer(layers: DeckGLLayer[], id: number | string | undefined) {
    console.log(`removing layer with id ${id}`);
    if (!id) {
        return layers;
    }
    return layers.filter((x) => {
        return x.id !== id;
    });
}

/**
 * Retrieves the coordinates and zoom level of the current port.
 *
 * @returns {Object} An object containing the latitude, longitude, and zoom level of the port.
 */
export function getPortCoordinate(port: PortType) {
    return {
        //@ts-ignore
        latitude: parseFloat(port.latitude),
        //@ts-ignore
        longitude: parseFloat(port.longitude),
        zoom: port.zoom,
    };
}

/**
 * Converts longitude and latitude to screen coordinates based on the current view state.
 *
 * @param {number} longitude - The longitude of the point.
 * @param {number} latitude - The latitude of the point.
 * @param {any} viewState - The current view state of the map.
 * @returns {Object} The screen coordinates (x, y).
 */
export function getScreenCoordinates(longitude: number, latitude: number, viewState: any) {
    const viewport = new WebMercatorViewport(viewState);
    const [x, y] = viewport.project([longitude, latitude]);
    return { x, y };
}

export function getCoordinatesFromScreenXY(x: number, y: number, viewState: any) {
    const viewport = new WebMercatorViewport(viewState);
    const [longitude, latitude] = viewport.unproject([x, y]);
    return { longitude, latitude };
}

/**
 * Returns the layer provider based on the background type (Google, WMS, or WMTS).
 *
 * @param {BackgroundType | undefined} background - The background data.
 * @param {number} index - The index of the layer.
 * @returns {TileLayer | WMSLayer | undefined | Any} The corresponding layer provider for the background type.
 */
 export function getLayerProvider(
    coordinate: CoordinateArray | null,
    background: BackgroundType | undefined,
    index: number
) {
    if (!background) {
        return;
    }

    switch (background.provider) {
        case 'XYZ':
            return new TileLayer({
                id: `B-${background.id}`,
                //@ts-ignore
                type: background.type,
                data: background.url,
                index: index,
                minZoom: 0,
                maxZoom: 21,
                tileSize: 256,
                //@ts-ignore
                renderSubLayers: (props: { tile: any; data: string }) => {
                    const {
                        bbox: { west, south, east, north },
                    } = props.tile;
                    //@ts-ignore
                    return new BitmapLayer(props, {
                        //@ts-ignore
                        data: [{ position: coordinate, color: [255, 0, 0], radius: 100 }],
                        image: props.data,
                        bounds: [west, south, east, north],
                    });
                },
            });
        case 'WMS':
            return new WMSLayer({
                coordinateystem: COORDINATE_SYSTEM.METER_OFFSETS,
                id: `B-${background.id}`,
                radiusPixels: 3,
                srs: 'EPSG:3857',
                index: index,
                type: background.type,
                data: `${background.url}?x-api-key=${background.key}`,
                serverType: 'geoserver',
                layers: [background?.meta_data?.layer || ''],
            });

        case 'WMTS': {
            const blobToImage = (blob: Blob): Promise<HTMLImageElement> => {
                return new Promise((resolve, reject) => {
                    const image = new Image();

                    image.onload = () => {
                        URL.revokeObjectURL(image.src);
                        resolve(image);
                    };

                    image.onerror = reject;
                    image.src = URL.createObjectURL(blob);
                });
            };

            const urlTemplate = `${background.url}?x-api-key=${background.key}&layer=${background.meta_data?.layer}&style=default&tilematrixset=${background.meta_data?.tileMatrixSet}&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fpng&TileMatrix={z}&TileCol={x}&TileRow={y}`;

            return new TileLayer({
                id: `B-${background.id}`,
                //@ts-ignore
                type: background.type,
                data: urlTemplate,
                srs: 'EPSG:3857',
                index,
                minZoom: 0,
                maxZoom: 21,
                tileSize: 256,
                // @ts-ignore
                async getTileData(tile: any) {
                    const { url, signal } = tile;

                    for (let retry = 0; retry < 3; retry++) {
                        try {
                            const response = await fetch(url, { signal });
                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}`);
                            }
                            const blob = await response.blob();
                            if (!blob.type.startsWith('image/')) {
                                throw new Error(`Unexpected content type: ${blob.type}`);
                            }
                            return await blobToImage(blob);
                        } catch (e) {
                            if (signal.aborted) {
                                throw e;
                            }
                            console.warn(`Retry ${retry + 1}:`, url);
                            if (retry < 2) {
                                await new Promise((resolve) => setTimeout(resolve, 500));
                            }
                        }
                    }

                    throw new Error(`Failed to load tile: ${url}`);
                },

                //@ts-ignore
                renderSubLayers: (props) => {
                    const {
                        bbox: { west, south, east, north },
                    } = props.tile;

                    return new BitmapLayer({
                        id: `${props.tile.id}-bitmap-${background.meta_data?.layer}`,
                        image: props.data,
                        bounds: [west, south, east, north],
                    });
                },
            });
        }
        default:
            break;
    }
}

export function filterBySection(data: Record<string, any>, section: string) {
    return Object.entries(data)
        .filter(([_, value]) => value.section === section)
        .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {} as Record<string, any>);
}
